import type { UseQueryResult } from '@tanstack/react-query'
import type { ReactNode } from 'react'

type BaseQueryMatcherProps<TData, TError> = {
    query: UseQueryResult<TData, TError>
    Loading: ReactNode
    Errored: ReactNode | ((error: TError) => ReactNode)
}

type QueryMatcherProps<TData, TError, TEmpty> = BaseQueryMatcherProps<TData, TError> & {
    Empty?: TEmpty
    children: (
        data: TEmpty extends undefined
            ? UseQueryResult<TData, TError>
            : UseQueryResult<TData, TError> & {
                  data: NonNullable<TData>
              }
    ) => ReactNode
}

/**
 * A component that handles different states of a query and renders appropriate UI.
 *
 * Useful for rendering different components based on loading, error, empty, and success states.
 *
 * @example ```jsx
 * return (
 *   <QueryMatcher
 *     query={someQuery}
 *     Loading={<LoadingSpinner />}
 *     Errored={error => <ErrorMessage error={error} />}
 *     Empty={<EmptyState />}
 *   >
 *     {({ data }) => <DataDisplay data={data} />}
 *   </QueryMatcher>
 * )
 * ```
 *
 * If you don't provide an `Empty` component, the data in children will be typed as possibly undefined.
 * If you do provide an `Empty` component, the data will be typed as non-nullable.
 *
 * @param query - The query result from react-query
 * @param Loading - Component to show during loading state
 * @param Errored - Component or render function to show during error state
 * @param Empty - Optional component to show when data is empty/null/undefined
 * @param children - Render function that receives the query result and returns a React node
 */
export default function QueryMatcher<
    TData,
    TError = unknown,
    TEmpty extends ReactNode = undefined,
>({ query, Loading, Errored, Empty, children }: QueryMatcherProps<TData, TError, TEmpty>) {
    if (!query.isInitialLoading && !query.data && !query.isError) {
        return null
    }

    if (query.isLoading) {
        return Loading
    }

    if (query.isError) {
        return typeof Errored === 'function' ? Errored(query.error) : Errored
    }

    const isEmpty =
        query.data === undefined ||
        query.data === null ||
        (Array.isArray(query.data) && query.data.length === 0)

    if (isEmpty && Empty) {
        return Empty
    }

    return children({
        ...query,
        data: query.data as NonNullable<typeof query.data>,
    })
}
